import {} from "@/api/model";
import {
  type AdminFacilityAsset,
  type AdminServiceAvailability as ApiAdminServiceAvailabilityForFacilityAsset,
  type PaginatedAdminFacilityAssetList,
  type AdminDateSpecificAvailability as ApiAdminDateSpecificAvailabilityForFacilityAsset,
  type AdminFacilityAssetNewAssignmentItem,
  type WithinServiceFacilityAssetAssignment,
} from "@/api/model";

import type { FacilityAssetDisplayItem, FacilityAssetFormData } from "./types";

import {
  translateAdminServiceAvailability,
  translateApiDateSpecificToFrontend,
  translateServiceAvailabilityToNewApiPayload,
  translateDateSpecificAvailabilityToApiPayload,
} from "./translator";

/**
 * Translates a single AdminFacilityAsset from the API to FacilityAssetDisplayItem for frontend display.
 */
export function translateAdminFacilityAssetToDisplayItem(
  apiAsset: AdminFacilityAsset
): FacilityAssetDisplayItem {
  return {
    id: apiAsset.id,
    name: apiAsset.name,
    servicesCount: apiAsset.services_count || 0,
    serviceAvailabilities:
      apiAsset.service_availabilities?.map((avail) =>
        translateAdminServiceAvailability(
          avail as ApiAdminServiceAvailabilityForFacilityAsset
        )
      ) ?? [],
    rawDateSpecificAvailabilities:
      apiAsset.date_specific_availabilities?.map(
        (dsa: ApiAdminDateSpecificAvailabilityForFacilityAsset) => ({ ...dsa })
      ) ?? [],
  };
}

/**
 * Translates a paginated list of AdminFacilityAsset from the API
 * to an array of FacilityAssetDisplayItem.
 */
export function translatePaginatedFacilityAssetListToDisplayItems(
  paginatedList: PaginatedAdminFacilityAssetList | undefined
): FacilityAssetDisplayItem[] {
  if (!paginatedList?.results) {
    return [];
  }
  return paginatedList.results.map(translateAdminFacilityAssetToDisplayItem);
}

/**
 * Translates FacilityAssetFormData into the payload structure for creating an AdminFacilityAsset.
 * The return type `Partial<AdminFacilityAsset>` is used because for create,
 * we don't send readonly fields like id, created_at, archived_at etc.
 * Orval's NonReadonly<AdminFacilityAsset> is more precise.
 */
export function translateFacilityAssetFormDataToApiPayload(
  formData: FacilityAssetFormData
): Pick<
  AdminFacilityAsset,
  | "name"
  | "new_service_assignments"
  | "service_availabilities"
  | "date_specific_availabilities"
> {
  const newServiceAssignments: AdminFacilityAssetNewAssignmentItem[] =
    formData.assignedServiceIds.map((serviceId) => ({
      service_id: serviceId,
    }));

  const serviceAvailabilitiesForApi = formData.serviceAvailabilities
    .map(translateServiceAvailabilityToNewApiPayload)
    .filter(
      (avail) => avail !== null
    ) as ApiAdminServiceAvailabilityForFacilityAsset[];

  const dateSpecificAvailabilitiesForApi = formData.dateSpecificOverrides
    .map(translateDateSpecificAvailabilityToApiPayload)
    .filter(
      (override) => override !== null
    ) as ApiAdminDateSpecificAvailabilityForFacilityAsset[];

  return {
    name: formData.name,
    new_service_assignments: newServiceAssignments,
    service_availabilities: serviceAvailabilitiesForApi,
    date_specific_availabilities: dateSpecificAvailabilitiesForApi,
  };
}

/**
 * Translates a single AdminFacilityAsset from the API to FacilityAssetFormData for form population.
 */
export function translateAdminFacilityAssetToFormData(
  apiAsset: AdminFacilityAsset
): FacilityAssetFormData {
  return {
    id: apiAsset.id,
    name: apiAsset.name || "",
    assignedServiceIds:
      apiAsset.service_assignments?.map((sa) => sa.service_id) || [],
    serviceAvailabilities: apiAsset.service_availabilities
      ? apiAsset.service_availabilities.map((avail) =>
          translateAdminServiceAvailability(
            avail as ApiAdminServiceAvailabilityForFacilityAsset
          )
        )
      : [],
    dateSpecificOverrides: apiAsset.date_specific_availabilities
      ? apiAsset.date_specific_availabilities.map((override) =>
          translateApiDateSpecificToFrontend(
            override as ApiAdminDateSpecificAvailabilityForFacilityAsset
          )
        )
      : [],
  };
}

/**
 * Translates FacilityAssetFormData into the payload structure for creating/updating an AdminFacilityAsset.
 * The Orval type for update is NonReadonly<AdminFacilityAsset>.
 *
 * IMPORTANT: The API for update expects the *full* list of current service_availabilities
 * and date_specific_availabilities, not just deltas. If existing ones are omitted, they will be deleted.
 */
export function translateFacilityAssetFormDataToApiUpdatePayload(
  formData: FacilityAssetFormData
): Pick<
  AdminFacilityAsset,
  | "name"
  | "service_availabilities"
  | "date_specific_availabilities"
  | "new_service_assignments"
> {
  const newServiceAssignments: AdminFacilityAssetNewAssignmentItem[] =
    formData.assignedServiceIds.map((serviceId) => ({ service_id: serviceId }));

  const serviceAvailabilitiesForApi = formData.serviceAvailabilities
    .map(translateServiceAvailabilityToNewApiPayload)
    .filter(
      (avail) => avail !== null
    ) as ApiAdminServiceAvailabilityForFacilityAsset[];

  const dateSpecificAvailabilitiesForApi = formData.dateSpecificOverrides
    .map(translateDateSpecificAvailabilityToApiPayload)
    .filter(
      (override) => override !== null
    ) as ApiAdminDateSpecificAvailabilityForFacilityAsset[];

  return {
    name: formData.name,
    new_service_assignments: newServiceAssignments,
    service_availabilities: serviceAvailabilitiesForApi,
    date_specific_availabilities: dateSpecificAvailabilitiesForApi,
  };
}

/**
 * Translates an array of facility asset IDs into the API payload structure for service assignments.
 * @param assetIds - Array of facility asset UUIDs.
 * @returns An array of objects structured for the API.
 */
export function translateFacilityAssetAssignmentsToApiPayload(
  assetIds: string[] | undefined
): { facility_asset_id: string }[] {
  if (!assetIds) {
    return [];
  }
  return assetIds.map((id) => ({
    facility_asset_id: id,
  }));
}

/**
 * Extracts facility asset IDs from an API service's `facility_asset_assignments`.
 * @param assignments - The array of assignment objects from the API.
 * @returns An array of facility asset UUIDs.
 */
export function translateApiFacilityAssetAssignmentsToIds(
  assignments: WithinServiceFacilityAssetAssignment[] | undefined
): string[] {
  if (!assignments) {
    return [];
  }
  return assignments.map((a) => a.facility_asset_id);
}
